import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { ContactAlerts, HoneypotField, useContactSubmit } from '../services/contactService';

const DemoRequestDialog = ({ open, onClose }) => {
  const { user } = useAuth();
  const { submit, loading, error, sent, resetStatus } = useContactSubmit();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    comments: '',
    phoneCall: false,
    emailFollowup: false,
    preferredTime: '',
    website: '',
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleSubmit = async () => {
    await submit({
      type: 'demo',
      name: formData.name,
      email: formData.email,
      company: formData.company,
      phone: formData.phone,
      comments: formData.comments,
      phoneCall: formData.phoneCall,
      emailFollowup: formData.emailFollowup,
      preferredTime: formData.preferredTime,
      website: formData.website,
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      company: '',
      phone: '',
      comments: '',
      phoneCall: false,
      emailFollowup: false,
      preferredTime: '',
      website: '',
    });
    resetStatus();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.name.trim() && 
           formData.email.trim() && 
           formData.company.trim() && 
           formData.phone.trim();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Request a Demo
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <ContactAlerts error={error} sent={sent} />
          <HoneypotField
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Name"
              required
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={user?.user_metadata?.first_name && user?.user_metadata?.last_name 
                ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`
                : user?.email || ''}
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={user?.email || ''}
            />
          </Box>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField
              fullWidth
              label="Company"
              required
              value={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
            />
            <TextField
              fullWidth
              label="Phone Number"
              required
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
            />
          </Box>

          {/* Contact Preferences */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Contact Preferences
            </Typography>
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.phoneCall}
                  onChange={(e) => handleCheckboxChange('phoneCall', e.target.checked)}
                />
              }
              label="Phone"
            />
            
            {formData.phoneCall && (
              <Box sx={{ ml: 4, mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Preferred Call Time</InputLabel>
                  <Select
                    value={formData.preferredTime}
                    onChange={(e) => handleInputChange('preferredTime', e.target.value)}
                    label="Preferred Call Time"
                  >
                    <MenuItem value="Morning (8 AM - 12 PM)">Morning (8 AM - 12 PM)</MenuItem>
                    <MenuItem value="Afternoon (12 PM - 5 PM)">Afternoon (12 PM - 5 PM)</MenuItem>
                    <MenuItem value="Evening (5 PM - 8 PM)">Evening (5 PM - 8 PM)</MenuItem>
                    <MenuItem value="Flexible - Any time works">Flexible - Any time works</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            )}
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.emailFollowup}
                  onChange={(e) => handleCheckboxChange('emailFollowup', e.target.checked)}
                />
              }
              label="Email"
            />
          </Box>

          <TextField
            fullWidth
            label="Additional Comments"
            multiline
            rows={4}
            value={formData.comments}
            onChange={(e) => handleInputChange('comments', e.target.value)}
            placeholder="Tell us about your specific needs, questions, or any other information..."
            helperText={`${formData.comments.length}/500 characters`}
            inputProps={{ maxLength: 500 }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {sent ? 'Close' : 'Cancel'}
        </Button>
        {!sent && (
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary"
            disabled={!isFormValid() || loading}
          >
            {loading ? 'Sending...' : 'Send Request'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DemoRequestDialog;


